import React, { useState } from 'react';
import ABTestNavigation from './abtest/ABTestNavigation';

const ABTestingDemo: React.FC = () => {
  const [showDemo, setShowDemo] = useState(false);

  if (showDemo) {
    return <ABTestNavigation onClose={() => setShowDemo(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-700 flex items-center justify-center">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          
          <h1 className="text-4xl font-bold text-content mb-4">
            Automated A/B Testing Suite
          </h1>
          
          <p className="text-xl text-content-secondary mb-8 max-w-2xl mx-auto">
            Experience the power of AI-driven A/B testing with real-time monitoring, 
            automated winner detection, and intelligent implementation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🔴</span>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">Live Monitoring</h3>
              <p className="text-content-secondary text-sm">
                Real-time performance tracking with automated alerts and winner detection
              </p>
            </div>

            <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">AI Automation</h3>
              <p className="text-content-secondary text-sm">
                Intelligent winner implementation with safety controls and rollback mechanisms
              </p>
            </div>

            <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🧠</span>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">Smart Insights</h3>
              <p className="text-content-secondary text-sm">
                AI-powered insights and recommendations based on historical patterns
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => setShowDemo(true)}
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Launch Live Demo
            </button>
            
            <div className="text-sm text-content-muted">
              No setup required • Interactive demo with sample data
            </div>
          </div>

          <div className="mt-16 bg-surface rounded-lg shadow-sm border border-line p-8">
            <h2 className="text-2xl font-bold text-content dark:text-gray-100 mb-6">What You'll See</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-lg font-semibold text-content dark:text-gray-100 mb-3">🔴 Live Monitoring Dashboard</h3>
                <ul className="space-y-2 text-content-secondary dark:text-gray-400">
                  <li className="flex items-start">
                    <span className="text-blue-500 dark:text-blue-400 mr-2">•</span>
                    Real-time performance metrics and confidence tracking
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 dark:text-blue-400 mr-2">•</span>
                    Automated winner detection with configurable thresholds
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 dark:text-blue-400 mr-2">•</span>
                    Implementation status and rollback controls
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 dark:text-blue-400 mr-2">•</span>
                    Live alerts and notification system
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-content mb-3">🧠 AI Insights Engine</h3>
                <ul className="space-y-2 text-content-secondary">
                  <li className="flex items-start">
                    <span className="text-purple-500 dark:text-purple-400 mr-2">•</span>
                    Statistical analysis and significance testing
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 dark:text-purple-400 mr-2">•</span>
                    Pattern recognition from historical data
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 dark:text-purple-400 mr-2">•</span>
                    Business impact assessment and recommendations
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 dark:text-purple-400 mr-2">•</span>
                    Automated insights generation and categorization
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ABTestingDemo;